import { faireBaseUrl } from "@faire/web--source/common/routes/faireBaseUrl";
import { isPrerender } from "@faire/web--source/common/user-agent/isPrerender";
import { Location } from "history";
import { useMemo } from "react";
import { useLocation } from "react-router-dom";

import { ProductLinkProps } from "@faire/retailer/features/MarketplaceItems/ProductTile";
import { canAddToCart } from "@faire/retailer/lib/canAddToCart";
import { generateProductLinkPropsForLoggedOutRetailer } from "@faire/retailer/lib/generateProductLinkPropsForLoggedOutRetailer";
import { getIsBrandPreview } from "@faire/retailer-visitor-shared/lib/getIsBrandPreview";
import { isAdmin } from "@faire/retailer/lib/isAdmin";
import { ProductPagePaths } from "@faire/retailer/paths/ProductPage";
import { generateProductUrlForBrandPage } from "@faire/retailer/views/brand/utils/generateProductUrlForBrandPage";

export function generateProductBrandPageLinkPropsHelper(
  productToken: string,
  location: Location,
  brandToken?: string,
  productOptionToken?: string
): ProductLinkProps {
  const productPageLink = generateProductUrlForBrandPage(
    productToken,
    productOptionToken
  );
  if (canAddToCart() || getIsBrandPreview() || isAdmin()) {
    return { productUrl: productPageLink };
  }

  return (
    generateProductLinkPropsForLoggedOutRetailer({
      productToken: productToken,
      brandToken: brandToken,
      location,
      productOptionToken: productOptionToken,
      isShowRefB: true,
    }) ?? { productUrl: productPageLink }
  );
}

export const useProductUrlBrandPage = ({
  productToken,
  selectedProductOptionToken,
  brandToken,
}: {
  productToken: string;
  selectedProductOptionToken: string | undefined;
  brandToken: string | undefined;
}): ProductLinkProps => {
  const location = useLocation();
  const productUrl = useMemo(() => {
    if (!productToken) {
      return { productUrl: faireBaseUrl() };
    }
    // For SEO discoverability, expose "real" product page links instead of a signup brand page link for bot.
    if (isPrerender()) {
      return { productUrl: ProductPagePaths.index(productToken) };
    }

    return (
      generateProductBrandPageLinkPropsHelper(
        productToken,
        location,
        brandToken,
        selectedProductOptionToken
      ) ?? {
        productUrl: ProductPagePaths.index(productToken, {
          productOptionToken: selectedProductOptionToken,
        }),
      }
    );
  }, [location, brandToken, productToken, selectedProductOptionToken]);
  return productUrl;
};
